import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ShoppingBag, Flame, Zap, Wheat, Apple, Droplets, X } from "lucide-react";
import OrderModal from "@/components/OrderModal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUTRIENT_META = {
  calories: { icon: Flame, label: "Calories" },
  protein: { icon: Zap, label: "Protein" },
  carbs: { icon: Wheat, label: "Carbs" },
  fiber: { icon: Apple, label: "Fiber" },
  sugar: { icon: Droplets, label: "Sugar" },
  vitamin_c: { icon: Zap, label: "Vit C" },
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [orderOpen, setOrderOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const categories = ["All", ...new Set(products.map((p) => p.category))];
  const filtered = activeFilter === "All" ? products : products.filter((p) => p.category === activeFilter);

  return (
    <main data-testid="products-page" style={{ background: "#FCF8F6" }}>
      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#A3B18A" }}
          >
            Our Menu
          </p>
          <h1
            className="text-5xl sm:text-6xl tracking-tighter leading-none font-medium mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "#283618" }}
          >
            Products
          </h1>
          <p
            className="text-base md:text-lg leading-relaxed max-w-xl"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
          >
            Every juice is cold-pressed fresh daily with no added sugar, water,
            or preservatives. Designed for same-day consumption.
          </p>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mt-10" data-testid="product-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveFilter(cat)}
                className="rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all duration-300"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: activeFilter === cat ? "#283618" : "transparent",
                  color: activeFilter === cat ? "#FCF8F6" : "#6F5E58",
                  border: `1.5px solid ${activeFilter === cat ? "#283618" : "rgba(163, 177, 138, 0.3)"}`,
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {filtered.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="group cursor-pointer product-card-hover"
                onClick={() => setSelected(product)}
              >
                <div
                  className="rounded-3xl overflow-hidden mb-5 aspect-square relative"
                  style={{ background: "#F5D5CB" }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-image w-full h-full object-cover transition-transform duration-700"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#283618]/0 group-hover:bg-[#283618]/20 transition-all duration-500 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span
                      className="text-xs font-semibold uppercase tracking-wider px-5 py-2 rounded-full"
                      style={{ background: "#FCF8F6", color: "#283618", fontFamily: "'Manrope', sans-serif" }}
                    >
                      View Details
                    </span>
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[10px] uppercase tracking-[0.2em] font-semibold"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "#A3B18A" }}
                      >
                        {product.category}
                      </span>
                      {product.quantity && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-semibold"
                          style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
                        >
                          {product.quantity}
                        </span>
                      )}
                    </div>
                    <h3
                      className="text-2xl font-medium mt-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A2A2A" }}
                    >
                      {product.name}
                    </h3>
                  </div>
                  <span
                    className="text-sm font-semibold mt-1 whitespace-nowrap"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "#283618" }}
                  >
                    {product.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section
        className="py-20 md:py-24 text-center"
        style={{ background: "#F5D5CB" }}
        data-testid="products-cta"
      >
        <div className="max-w-2xl mx-auto px-6">
          <h2
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "#283618" }}
          >
            Can't decide?
          </h2>
          <p
            className="text-sm md:text-base leading-relaxed mb-8"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
          >
            Browse our delivery partners and order your favourite juice right now.
          </p>
          <Button
            data-testid="products-order-btn"
            onClick={() => setOrderOpen(true)}
            className="rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 mx-auto transition-all duration-300 hover:scale-105"
            style={{ background: "#283618", color: "#FCF8F6", fontFamily: "'Manrope', sans-serif" }}
          >
            <ShoppingBag size={16} />
            Order Now
          </Button>
        </div>
      </section>

      {/* Product Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent
          className="sm:max-w-2xl rounded-3xl border-none p-0 overflow-hidden"
          style={{ background: "#FCF8F6" }}
          data-testid="product-detail-modal"
        >
          {selected && (
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {/* Image */}
              <div className="aspect-square sm:aspect-auto sm:h-full" style={{ background: "#F5D5CB" }}>
                <img
                  src={selected.image}
                  alt={selected.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Details */}
              <div className="p-6 md:p-8 flex flex-col justify-between overflow-y-auto max-h-[80vh]">
                <div>
                  {/* Category + Quantity */}
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "#283618", background: "rgba(163, 177, 138, 0.25)" }}
                    >
                      {selected.category}
                    </span>
                    {selected.quantity && (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58", background: "rgba(111, 94, 88, 0.1)" }}
                      >
                        {selected.quantity}
                      </span>
                    )}
                  </div>

                  {/* Name + Price */}
                  <div className="flex items-start justify-between mb-3">
                    <h2
                      className="text-2xl md:text-3xl font-medium"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: "#283618" }}
                    >
                      {selected.name}
                    </h2>
                    <span
                      className="text-base font-semibold whitespace-nowrap ml-4"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "#283618" }}
                    >
                      {selected.price}
                    </span>
                  </div>

                  {/* Description */}
                  <p
                    className="text-sm leading-relaxed mb-5"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
                  >
                    {selected.description}
                  </p>

                  {/* Ingredients */}
                  <p
                    className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
                  >
                    Ingredients
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {selected.ingredients?.map((ing) => (
                      <span
                        key={ing}
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "#283618", background: "rgba(163, 177, 138, 0.15)" }}
                      >
                        {ing}
                      </span>
                    ))}
                  </div>

                  {/* Nutrients - detailed */}
                  {selected.nutrients && (
                    <>
                      <p
                        className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
                      >
                        Nutrition per serving
                      </p>
                      <div className="grid grid-cols-3 gap-2 mb-5">
                        {Object.entries(selected.nutrients).map(([key, val]) => {
                          const meta = NUTRIENT_META[key];
                          const Icon = meta?.icon || Flame;
                          return (
                            <div
                              key={key}
                              className="text-center rounded-xl py-2 px-1"
                              style={{ background: "rgba(163, 177, 138, 0.1)" }}
                            >
                              <Icon size={12} color="#A3B18A" className="mx-auto mb-0.5" />
                              <p className="text-[11px] font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "#283618" }}>
                                {val}
                              </p>
                              <p className="text-[8px] uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}>
                                {meta?.label || key}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Nutrients - highlights */}
                  {selected.nutrient_highlights?.length > 0 && !selected.nutrients && (
                    <>
                      <p
                        className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
                      >
                        Key Nutrients
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {selected.nutrient_highlights.map((n) => (
                          <span
                            key={n}
                            className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                            style={{ fontFamily: "'Manrope', sans-serif", color: "#283618", background: "rgba(240, 183, 164, 0.25)" }}
                          >
                            {n}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Order Button */}
                <Button
                  data-testid="modal-order-btn"
                  onClick={() => { setSelected(null); setOrderOpen(true); }}
                  className="rounded-full w-full py-3 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105"
                  style={{ background: "#283618", color: "#FCF8F6", fontFamily: "'Manrope', sans-serif" }}
                >
                  <ShoppingBag size={14} />
                  Order Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
    </main>
  );
}
